/**
 * linkParse.js — Turn a sportsbook "share bet" link into a DRAFT bet
 * record. Two halves:
 *
 *   classifyShareUrl(url)  — cheap, offline: which book is this link
 *                            from, and what slip/bet id does it carry?
 *   parseSharePage(html, book) — given the share page's HTML, extract
 *                            the bet fields into a draft.
 *
 * PRODUCTION NOTE (important): the browser/app cannot fetch a
 * sportsbook share page directly — CORS blocks it, and the books'
 * pages expect a full browser session. In production the fetch happens
 * SERVER-SIDE in a small Netlify function: the client posts the URL,
 * the function fetches the page, runs parseSharePage() from this file,
 * and returns the draft for the confirm card. That keeps this file
 * identical across web/app/server; only the fetch moves.
 *
 * Parsers are PER-BOOK and WILL need occasional upkeep: books change
 * their share-page markup without notice. The strategy below is
 * layered to degrade gracefully — embedded JSON first (most stable),
 * Open Graph / meta text second — and when everything fails the draft
 * comes back null and the UI falls back to text/manual entry with the
 * book already known from the URL. Never scrape behind a login; share
 * pages are public by design (the user was given the link to share).
 */

import { createBet } from '../engine/model.js';

/** Books whose share links we recognize, by registrable domain. */
export const SHARE_BOOKS = Object.freeze([
  { key: 'draftkings', name: 'DraftKings', domains: ['draftkings.com'] },
  { key: 'fanduel', name: 'FanDuel', domains: ['fanduel.com'] },
  { key: 'betmgm', name: 'BetMGM', domains: ['betmgm.com'] },
  { key: 'caesars', name: 'Caesars', domains: ['caesars.com'] },
  { key: 'betrivers', name: 'BetRivers', domains: ['betrivers.com'] },
  { key: 'espnbet', name: 'ESPN BET', domains: ['espnbet.com'] },
  { key: 'fanatics', name: 'Fanatics', domains: ['fanatics.com'] },
  { key: 'bet365', name: 'bet365', domains: ['bet365.com'] },
  { key: 'hardrock', name: 'Hard Rock Bet', domains: ['hardrock.bet'] },
]);

const LP_ID_PARAMS = ['betid', 'bet', 'slipid', 'slip', 'shareid', 'share', 'ticket', 'id'];

/**
 * Classify a pasted URL. Returns:
 *   { url, host, book: {key,name}|null, recognized, shareId, params }
 * Unknown domains -> { book: null, recognized: false } so the UI can
 * say "we don't recognize that book's links yet" and fall back to
 * pasting the bet as text — never an exception.
 */
export function classifyShareUrl(rawUrl) {
  const url = String(rawUrl ?? '').trim();
  const fallback = { url, host: null, book: null, recognized: false, shareId: null, params: {} };
  const m = url.match(/^(?:[a-z][a-z0-9+.-]*:\/\/)?([^/?#]+)([^#]*)/i);
  if (!m) return fallback;
  const host = m[1].toLowerCase();
  const rest = m[2] || '';

  const book = SHARE_BOOKS.find((b) =>
    b.domains.some((d) => host === d || host.endsWith('.' + d)));
  if (!book) return { ...fallback, host };

  // Query params (parsed by hand to stay runtime-agnostic).
  const params = {};
  const qIndex = rest.indexOf('?');
  const path = qIndex >= 0 ? rest.slice(0, qIndex) : rest;
  if (qIndex >= 0) {
    for (const pair of rest.slice(qIndex + 1).split('&')) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      const k = decodeURIComponent(eq >= 0 ? pair.slice(0, eq) : pair).toLowerCase();
      const v = eq >= 0 ? decodeURIComponent(pair.slice(eq + 1)) : '';
      params[k] = v;
    }
  }

  // Slip/bet id: a known query param wins; otherwise the longest
  // id-looking path segment (>= 6 chars, mixed alphanumerics).
  let shareId = null;
  for (const key of LP_ID_PARAMS) {
    if (params[key]) { shareId = params[key]; break; }
  }
  if (!shareId) {
    const segments = path.split('/').filter(Boolean);
    const candidates = segments.filter((s) => /^[A-Za-z0-9_-]{6,}$/.test(s) && /\d/.test(s));
    if (candidates.length) shareId = candidates[candidates.length - 1];
  }

  return {
    url,
    host,
    book: { key: book.key, name: book.name },
    recognized: true,
    shareId,
    params,
  };
}

// ------------------------------------------------------------------
// Share-page parsing
// ------------------------------------------------------------------

const LP_SELECTION_KEYS = ['selection', 'displayselection', 'selectionname', 'pick', 'outcomename'];
const LP_EVENT_KEYS = ['event', 'eventname', 'matchup', 'game'];
const LP_MARKET_KEYS = ['market', 'marketname', 'bettype', 'type'];
const LP_LINE_KEYS = ['line', 'handicap', 'points'];
const LP_ODDS_KEYS = ['oddsamerican', 'americanodds', 'odds', 'price'];
const LP_STAKE_KEYS = ['stake', 'wager', 'risk', 'amount'];
const LP_SPORT_KEYS = ['sport', 'sportname'];
const LP_LEGS_KEYS = ['legs', 'selections', 'picks'];

function lpGet(obj, keys) {
  for (const key of Object.keys(obj)) {
    if (keys.includes(key.toLowerCase())) return obj[key];
  }
  return undefined;
}

function lpLooksLikeBet(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  const hasSelection = lpGet(obj, LP_SELECTION_KEYS) !== undefined;
  const hasOdds = typeof lpGet(obj, LP_ODDS_KEYS) === 'number';
  const hasMarketOrLine = lpGet(obj, LP_MARKET_KEYS) !== undefined || lpGet(obj, LP_LINE_KEYS) !== undefined;
  return hasSelection && (hasOdds || hasMarketOrLine);
}

/** Depth-first search for the first object that looks like a bet. */
function lpFindBet(node, depth = 0) {
  if (depth > 8 || node === null || typeof node !== 'object') return null;
  if (lpLooksLikeBet(node)) return node;
  for (const value of Object.values(node)) {
    const hit = lpFindBet(value, depth + 1);
    if (hit) return hit;
  }
  return null;
}

function lpBetTypeFromMarket(marketText, selectionText) {
  const text = ((marketText ?? '') + ' ' + (selectionText ?? '')).toLowerCase();
  if (/spread|handicap|point spread|run line|puck line/.test(text)) return 'spread';
  if (/total|over\/under|o\/u/.test(text)) return 'total';
  if (/moneyline|money line|\bml\b|to win/.test(text)) return 'moneyline';
  if (/\b(over|under)\s*\d/.test(text)) return 'total';
  return null;
}

function lpDraftFromFields(fields, book, warnings) {
  const missing = [];
  const selectionText = fields.selection != null ? String(fields.selection) : '';
  let betType = fields.betType ?? lpBetTypeFromMarket(fields.market, selectionText);
  let line = typeof fields.line === 'number' ? fields.line : null;

  // Compose the selection the way the hub displays it ("Chiefs -3.5").
  let selection = selectionText;
  if (betType === 'spread' && line !== null && selectionText &&
      !/[+-]\d+(?:\.\d+)?\s*$/.test(selectionText)) {
    selection = selectionText + ' ' + (line > 0 ? '+' + line : '' + line);
  }

  const draft = createBet({
    book: book ?? undefined,
    sport: fields.sport ?? '',
    league: fields.sport ?? '',
    event: fields.event ?? '',
    selection,
    betType: betType ?? 'moneyline',
    line,
    oddsAmerican: typeof fields.odds === 'number' ? fields.odds : undefined,
    stake: typeof fields.stake === 'number' ? fields.stake : 0,
    status: 'draft',
    source: 'link',
    externalId: fields.shareId ?? undefined,
  });
  if (typeof fields.odds !== 'number') { draft.oddsAmerican = null; missing.push('odds'); }
  if (typeof fields.stake !== 'number') missing.push('stake');
  if (!book) missing.push('book');
  if (!selection) missing.push('selection');
  if (!betType) warnings.push('market type not stated on the share page — assumed moneyline');
  return { draft, missing, warnings };
}

/** Strategy 1: JSON embedded in <script> tags (incl. __NEXT_DATA__). */
function lpParseEmbeddedJson(html) {
  const scriptRe = /<script[^>]*type="application\/(?:ld\+)?json"[^>]*>([\s\S]*?)<\/script>/gi;
  let m;
  while ((m = scriptRe.exec(html)) !== null) {
    let data;
    try { data = JSON.parse(m[1].trim()); } catch { continue; }
    const betObj = lpFindBet(data);
    if (!betObj) continue;
    const legsRaw = lpGet(betObj, LP_LEGS_KEYS);
    if (Array.isArray(legsRaw) && legsRaw.length > 1) {
      // Parlay share: build the draft from the leg list.
      const legs = legsRaw.map((leg) => ({
        event: String(lpGet(leg, LP_EVENT_KEYS) ?? ''),
        selection: String(lpGet(leg, LP_SELECTION_KEYS) ?? ''),
        betType: lpBetTypeFromMarket(lpGet(leg, LP_MARKET_KEYS), lpGet(leg, LP_SELECTION_KEYS)) ?? 'moneyline',
        ...(typeof lpGet(leg, LP_LINE_KEYS) === 'number' ? { line: lpGet(leg, LP_LINE_KEYS) } : {}),
      }));
      return {
        fields: {
          selection: legs.map((l) => l.selection).join(' + '),
          event: legs.length + '-leg parlay',
          betType: 'parlay',
          legs,
          odds: lpGet(betObj, LP_ODDS_KEYS),
          stake: lpGet(betObj, LP_STAKE_KEYS),
          sport: lpGet(betObj, LP_SPORT_KEYS),
        },
        strategy: 'embedded-json',
      };
    }
    return {
      fields: {
        selection: lpGet(betObj, LP_SELECTION_KEYS),
        event: lpGet(betObj, LP_EVENT_KEYS),
        market: lpGet(betObj, LP_MARKET_KEYS),
        line: lpGet(betObj, LP_LINE_KEYS),
        odds: lpGet(betObj, LP_ODDS_KEYS),
        stake: lpGet(betObj, LP_STAKE_KEYS),
        sport: lpGet(betObj, LP_SPORT_KEYS),
      },
      strategy: 'embedded-json',
    };
  }
  return null;
}

/** Strategy 2: Open Graph / twitter meta text. */
function lpParseMeta(html) {
  const meta = {};
  const metaRe = /<meta[^>]+(?:property|name)="(og:title|og:description|twitter:description|description)"[^>]+content="([^"]*)"/gi;
  let m;
  while ((m = metaRe.exec(html)) !== null) meta[m[1]] = m[2];
  const text = [meta['og:description'], meta['twitter:description'], meta['og:title'], meta['description']]
    .filter(Boolean).join(' · ');
  if (!text) return null;

  const fields = {};
  // "Brewers ML (+140) — $50.00 at BetMGM · Milwaukee Brewers at Tampa Bay Rays"
  const selMatch = text.match(/^(.+?)\s*\(([+-]?\d+)\)/);
  if (selMatch) {
    fields.selection = selMatch[1].trim();
    fields.odds = parseInt(selMatch[2], 10);
  } else {
    const ouMatch = text.match(/\b(over|under)\s*(\d+(?:\.\d+)?)\s*\(([+-]?\d+)\)/i);
    if (ouMatch) {
      fields.selection = (ouMatch[1].toLowerCase() === 'over' ? 'Over ' : 'Under ') + ouMatch[2];
      fields.line = parseFloat(ouMatch[2]);
      fields.odds = parseInt(ouMatch[3], 10);
    }
  }
  const stakeMatch = text.match(/\$\s*(\d+(?:\.\d{1,2})?)/);
  if (stakeMatch) fields.stake = parseFloat(stakeMatch[1]);
  // The event is the "·"-separated segment that names two teams and
  // carries no odds/stake debris, e.g. "Milwaukee Brewers at Tampa Bay Rays".
  const eventSeg = text.split('·').map((s) => s.trim())
    .find((s) => /\s(?:at|vs\.?)\s/.test(s) && !s.includes('(') && !s.includes('$'));
  if (eventSeg) fields.event = eventSeg;
  if (!fields.selection && !fields.odds) return null;
  fields.market = /\bML\b/.test(fields.selection ?? '') ? 'Moneyline' : undefined;
  return { fields, strategy: 'meta-tags' };
}

/**
 * Extract bet fields from a share page's HTML into a draft.
 * `book` is the { key, name } from classifyShareUrl (or null).
 * Returns { draft|null, missing, warnings } — the same contract as
 * parseBetText, so the confirm UI treats every source identically.
 * A null draft means "nothing extractable": the caller falls back to
 * text/manual entry (the book and link id still carry over).
 */
export function parseSharePage(html, book) {
  const warnings = [];
  const page = String(html ?? '');
  if (!page) {
    return { draft: null, missing: [], warnings: ['empty share page — nothing to parse'] };
  }

  const jsonHit = lpParseEmbeddedJson(page);
  if (jsonHit) {
    warnings.push('parsed from the share page\'s embedded data (demo-grade parser — share pages change often)');
    const out = lpDraftFromFields(jsonHit.fields, book, warnings);
    if (jsonHit.fields.betType === 'parlay' && jsonHit.fields.legs) {
      out.draft.legs = jsonHit.fields.legs;
    }
    return out;
  }

  const metaHit = lpParseMeta(page);
  if (metaHit) {
    warnings.push('parsed from the share page\'s preview text only — verify every field');
    return lpDraftFromFields(metaHit.fields, book, warnings);
  }

  return {
    draft: null,
    missing: [],
    warnings: ['could not extract bet fields from this share page — paste the bet as text or enter it manually'],
  };
}
